import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GameState, PlayerStats, Decision, LifeStage, Achievement, GameSettings, GameEvent, GameNotification, PlayerStatus } from '../types/game';
import { gameEvents } from '../data/events';

const getEventColor = (type: string) => {
  const colors = {
    money: '#10b981',
    health: '#ef4444',
    social: '#8b5cf6',
    career: '#3b82f6',
    random: '#f59e0b',
  };
  return colors[type as keyof typeof colors] || '#6b7280';
};

const initialStats: PlayerStats = {
  health: 100, // Perfect health for babies
  happiness: 80, // Babies are generally happy
  wealth: 0, // No wealth for babies
  relationships: 80, // Strong family relationships
  education: 0, // No education yet
  career: 0, // No career yet
  debt: 0, // No debt at birth
  stress: 0, // No stress at birth
};

const initialStatus: PlayerStatus = {
  hasJob: false,
  isEducating: false,
  hasFamily: false,
  familyHappiness: 0,
  hasSpouse: false,
  hasChildren: false,
  isHealthy: true,
  healthStatus: 'Perfect',
  isInvesting: false,
  isZusRegistered: false,
  monthlyIncome: 0,
  monthlyExpenses: 0, // No expenses for babies
  netIncome: 0, // No net income for babies
  lastPaycheck: 0,
  hasPet: false,
  petHappiness: 0,
  livingLocation: 'With Parents', // Where you live
};

const initialGameState: GameState = {
  currentStage: 'childhood',
  currentAge: 0,
  currentMonth: 1,
  stats: initialStats,
  status: initialStatus,
  money: 2, // Start with $2 actual money
  completedDecisions: [],
  availableDecisions: [],
  mandatoryDecisions: [],
  gamePhase: 'intro',
  causeOfDeath: undefined,
  score: 0,
  unlockedAchievements: [],
  timeline: [],
  activeHabits: [],
  habits: [],
  notifications: [],
  isTimeRunning: false,
  timeSpeed: 1, // 1 month per interval (200ms)
  maxHabitSlots: 3,
  currentHabitSlots: 0,
  // Game Mode System
  gameMode: 'sandbox' as const,
  challengeScenario: undefined,
  educationalModule: undefined,
  modeObjectives: [],
  modeProgress: {
    completedObjectives: [],
    currentLesson: undefined,
    reflectionPoints: [],
  },
};

const defaultSettings: GameSettings = {
  difficulty: 'normal',
  autoSave: true,
  soundEnabled: true,
  animationsEnabled: true,
  theme: 'light',
};

interface GameStore extends GameState {
  settings: GameSettings;
  lifeStages: LifeStage[];
  decisions: Decision[];
  allAchievements: Achievement[];
  
  // UI State
  selectedDecision: Decision | null;
  isDecisionModalOpen: boolean;
  
  // Actions
  startGame: () => void;
  makeDecision: (decisionId: string, optionId: string) => void;
  updateStats: (changes: Partial<PlayerStats>) => void;
  advanceAge: () => void;
  addTimelineEvent: (event: any) => void;
  unlockAchievement: (achievementId: string) => void;
  resetGame: () => void;
  updateSettings: (settings: Partial<GameSettings>) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  completeGame: () => void;
  openDecisionModal: (decision: Decision) => void;
  closeDecisionModal: () => void;
  ensureDecisionsAvailable: () => void;
  addHabit: (habitId: string) => void;
  removeHabit: (habitId: string) => void;
  skipMandatoryDecision: (decisionId: string) => void;
  startTime: () => void;
  pauseTime: () => void;
  forwardOneYear: () => void;
  updateTimeSpeed: (speed: number) => void;
  addNotification: (notification: Omit<GameNotification, 'id' | 'timestamp'>) => void;
  markNotificationRead: (notificationId: string) => void;
  clearNotifications: () => void;
  processEvent: (event: GameEvent) => void;
  updateStatus: (statusUpdate: Partial<PlayerStatus>) => void;
  // Game Mode System
  setGameMode: (mode: 'sandbox' | 'challenge' | 'educational') => void;
  setChallengeScenario: (scenario: string) => void;
  setEducationalModule: (module: string) => void;
  setModeObjectives: (objectives: string[]) => void;
  updateModeProgress: (progress: Partial<{ completedObjectives: string[]; currentLesson?: string; reflectionPoints: string[] }>) => void;
  addMoney: (amount: number) => void;
  spendMoney: (amount: number) => boolean;
  processEventChoice: (eventId: string, choiceId: string) => void;
  calculateNetIncome: () => void;
}

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...initialGameState,
      settings: defaultSettings,
      lifeStages: [],
      decisions: [],
      allAchievements: [],
      selectedDecision: null,
      isDecisionModalOpen: false,

      startGame: () => {
        const state = get();
        
        // Start from birth (0 years old)
        set({
          gamePhase: 'playing',
          currentAge: 0,
          currentMonth: 1,
          currentStage: 'childhood',
          stats: initialStats,
          completedDecisions: [],
          availableDecisions: [], // No decisions available at birth
          mandatoryDecisions: [],
          score: 0,
          unlockedAchievements: [],
          timeline: [{
            id: 'birth',
            age: 0,
            type: 'milestone',
            title: 'You are born! 👶',
            description: 'Welcome to the world! Your life journey begins now.',
            impact: {},
            color: '#fbbf24',
          }],
          activeHabits: [],
          habits: state.habits,
          notifications: [{
            id: 'birth-notification',
            type: 'milestone',
            title: 'You are born! 👶',
            message: 'Welcome to the world! Your life journey begins now.',
            timestamp: Date.now(),
            read: false,
          }],
          isTimeRunning: true, // Start time running to fast forward to 18
          timeSpeed: 1, // 1 month per interval (200ms) - faster progression
          maxHabitSlots: 3,
          currentHabitSlots: 0,
        });
      },

      makeDecision: (decisionId: string, optionId: string) => {
        const state = get();
        const decision = state.decisions.find(d => d.id === decisionId);
        const option = decision?.options.find(o => o.id === optionId);
        
        if (!decision || !option) return;

        // Update stats
        const newStats = { ...state.stats };
        Object.entries(option.statChanges).forEach(([key, value]) => {
          if (value !== undefined) {
            newStats[key as keyof PlayerStats] = Math.max(0, Math.min(100, 
              newStats[key as keyof PlayerStats] + value
            ));
          }
        });

        // Add timeline event
        const timelineEvent = {
          id: `decision-${Date.now()}`,
          age: state.currentAge,
          type: 'decision' as const,
          title: decision.title,
          description: option.title,
          impact: option.statChanges,
          color: getCategoryColor(decision.category),
        };

        // Add new decisions based on the option's nextDecisions (limit to 2 total)
        const newAvailableDecisions = [...state.availableDecisions.filter(id => id !== decisionId)];
        if (option.nextDecisions) {
          option.nextDecisions.forEach(nextDecisionId => {
            if (!state.completedDecisions.includes(nextDecisionId) && 
                !newAvailableDecisions.includes(nextDecisionId) &&
                newAvailableDecisions.length < 2) { // Limit to max 2 decisions
              newAvailableDecisions.push(nextDecisionId);
            }
          });
        }

        // Update status based on decision
        const statusUpdate: Partial<PlayerStatus> = {};
        
        // Handle job-related decisions
        if (decision.category === 'career') {
          if (option.title.toLowerCase().includes('university') || option.title.toLowerCase().includes('degree')) {
            statusUpdate.isEducating = true;
            statusUpdate.educationLevel = 'University';
          } else if (option.title.toLowerCase().includes('work') || option.title.toLowerCase().includes('job') || 
                     option.title.toLowerCase().includes('workforce') || option.title.toLowerCase().includes('enter')) {
            statusUpdate.hasJob = true;
            statusUpdate.jobTitle = option.title;
          } else if (option.title.toLowerCase().includes('quit') || option.title.toLowerCase().includes('unemployed')) {
            statusUpdate.hasJob = false;
            statusUpdate.jobTitle = undefined;
          }
        }
        
        // Handle education decisions
        if (decision.category === 'education') {
          if (option.title.toLowerCase().includes('university') || option.title.toLowerCase().includes('degree')) {
            statusUpdate.isEducating = true;
            statusUpdate.educationLevel = 'University';
          } else if (option.title.toLowerCase().includes('trade') || option.title.toLowerCase().includes('certificate')) {
            statusUpdate.isEducating = true;
            statusUpdate.educationLevel = 'Trade School';
          } else if (option.title.toLowerCase().includes('work') || option.title.toLowerCase().includes('workforce')) {
            statusUpdate.isEducating = false;
            statusUpdate.hasJob = true;
            statusUpdate.jobTitle = 'Entry Level Worker';
          }
        }
        
        // Handle family decisions
        if (decision.category === 'relationships') {
          if (option.title.toLowerCase().includes('family') || option.title.toLowerCase().includes('children')) {
            statusUpdate.hasFamily = true;
            statusUpdate.familyHappiness = Math.min(100, state.status.familyHappiness + 20);
          } else if (option.title.toLowerCase().includes('single')) {
            statusUpdate.hasFamily = false;
            statusUpdate.familyHappiness = 0;
          }
        }
        
        // Handle health decisions
        if (decision.category === 'health') {
          if (newStats.health >= 80) {
            statusUpdate.isHealthy = true;
            statusUpdate.healthStatus = 'Excellent';
          } else if (newStats.health >= 60) {
            statusUpdate.isHealthy = true;
            statusUpdate.healthStatus = 'Good';
          } else if (newStats.health >= 40) {
            statusUpdate.isHealthy = true;
            statusUpdate.healthStatus = 'Fair';
          } else {
            statusUpdate.isHealthy = false;
            statusUpdate.healthStatus = 'Poor';
          }
        }
        
        // Handle investment decisions
        if (decision.category === 'finance') {
          if (option.title.toLowerCase().includes('invest')) {
            statusUpdate.isInvesting = true;
            statusUpdate.investmentType = option.title;
          } else if (option.title.toLowerCase().includes('save')) {
            statusUpdate.isInvesting = false;
            statusUpdate.investmentType = 'Savings';
          }
        }

        // Handle ZUS insurance decision
        if (decision.id === 'decision-zus') {
          if (option.id === 'register-zus') {
            statusUpdate.isZusRegistered = true;
            statusUpdate.monthlyExpenses = (statusUpdate.monthlyExpenses || state.status.monthlyExpenses) + 200; // ZUS contribution
          } else if (option.id === 'skip-zus') {
            statusUpdate.isZusRegistered = false;
          }
        }

        // Handle living arrangement decisions
        if (decision.id === 'decision-2') {
          if (option.id === 'dormitory') {
            statusUpdate.livingLocation = 'University Dormitory';
          } else if (option.id === 'apartment') {
            statusUpdate.livingLocation = 'Rented Apartment';
          } else if (option.id === 'parents') {
            statusUpdate.livingLocation = 'With Parents';
          } else if (option.id === 'house') {
            statusUpdate.livingLocation = 'Owned Home';
          }
        }

        // Handle costs - allow debt
        if (option.cost && option.cost > 0) {
          set(state => ({
            money: state.money - (option.cost || 0)
          }));
        }

        set({
          stats: newStats,
          status: { ...state.status, ...statusUpdate },
          completedDecisions: [...state.completedDecisions, decisionId],
          availableDecisions: newAvailableDecisions,
          timeline: [...state.timeline, timelineEvent],
          score: state.score + calculateScore(option.statChanges),
        });

        // Resume time if no more decisions are available
        if (newAvailableDecisions.length === 0 && state.mandatoryDecisions.length === 0) {
          get().startTime();
        }
      },

      updateStats: (changes: Partial<PlayerStats>) => {
        set(state => ({
          stats: {
            ...state.stats,
            ...Object.fromEntries(
              Object.entries(changes).map(([key, value]) => [
                key,
                Math.max(0, Math.min(100, value))
              ])
            )
          }
        }));
      },

      advanceAge: () => {
        set(state => {
          const newAge = state.currentAge + 1;
          const newStage = getLifeStageForAge(newAge);
          
          return {
            currentAge: newAge,
            currentStage: newStage,
            // Add some random events based on age
            stats: {
              ...state.stats,
              // More gradual health decline
              health: Math.max(0, state.stats.health - (newAge > 60 ? 0.5 : 0.2)),
            }
          };
        });
      },

      addTimelineEvent: (event) => {
        set(state => ({
          timeline: [...state.timeline, event]
        }));
      },

      unlockAchievement: (achievementId: string) => {
        set(state => ({
          unlockedAchievements: [...state.unlockedAchievements, achievementId]
        }));
      },

      resetGame: () => {
        set(initialGameState);
      },

      updateSettings: (newSettings) => {
        set(state => ({
          settings: { ...state.settings, ...newSettings }
        }));
      },

      pauseGame: () => {
        set({ gamePhase: 'paused' });
      },

      resumeGame: () => {
        set({ gamePhase: 'playing' });
      },

      completeGame: () => {
        set({ gamePhase: 'completed' });
      },

      openDecisionModal: (decision: Decision) => {
        set({ 
          selectedDecision: decision, 
          isDecisionModalOpen: true 
        });
      },

      closeDecisionModal: () => {
        set({ 
          selectedDecision: null, 
          isDecisionModalOpen: false 
        });
      },

      ensureDecisionsAvailable: () => {
        const state = get();
        const currentDecisions = state.availableDecisions.length + state.mandatoryDecisions.length;
        
        if (currentDecisions < 2 && state.decisions.length > 0) {
          const eligibleDecisions = state.decisions
            .filter(d => {
              // Check stage match
              const stageMatch = d.stage === state.currentStage || d.stage === 'any';
              // Check age match
              const ageMatch = (!d.minAge || state.currentAge >= d.minAge) && (!d.maxAge || state.currentAge <= d.maxAge);
              // Check if not already completed or available
              const notCompleted = !state.completedDecisions.includes(d.id);
              const notAvailable = !state.availableDecisions.includes(d.id) && !state.mandatoryDecisions.includes(d.id);
              // Check prerequisites
              const prerequisitesMet = !d.prerequisites || d.prerequisites.every(prereq => 
                state.completedDecisions.includes(prereq)
              );
              
              return stageMatch && ageMatch && notCompleted && notAvailable && prerequisitesMet;
            })
            .slice(0, 2 - currentDecisions) // Limit to max 2 total decisions
            .map(d => d.id);
          
          if (eligibleDecisions.length > 0) {
            set({ availableDecisions: [...state.availableDecisions, ...eligibleDecisions] });
          }
        }
      },

      addHabit: (habitId: string) => {
        const state = get();
        const habit = state.habits.find(h => h.id === habitId);
        if (habit && !state.activeHabits.includes(habitId)) {
          set({
            activeHabits: [...state.activeHabits, habitId],
            habits: state.habits.map(h => 
              h.id === habitId ? { ...h, isActive: true, startAge: state.currentAge } : h
            )
          });
        }
      },

      removeHabit: (habitId: string) => {
        const state = get();
        set({
          activeHabits: state.activeHabits.filter(id => id !== habitId),
          habits: state.habits.map(h => 
            h.id === habitId ? { ...h, isActive: false, endAge: state.currentAge } : h
          )
        });
      },

      skipMandatoryDecision: (decisionId: string) => {
        const state = get();
        set({
          mandatoryDecisions: state.mandatoryDecisions.filter(id => id !== decisionId),
          completedDecisions: [...state.completedDecisions, decisionId],
          gamePhase: state.mandatoryDecisions.length === 1 ? 'playing' : 'paused'
        });
      },

      startTime: () => {
        set({ isTimeRunning: true });
      },

      pauseTime: () => {
        set({ isTimeRunning: false });
      },

      forwardOneYear: () => {
        const state = get();
        const newAge = state.currentAge + 1;
        const newStage = getLifeStageForAge(newAge);
        
        set({
          currentAge: newAge,
          currentStage: newStage,
          currentMonth: 1,
        });
      },

      updateTimeSpeed: (speed: number) => {
        set({ timeSpeed: speed });
      },

      // Game Mode System
      setGameMode: (mode) => {
        set({ gameMode: mode });
      },

      setChallengeScenario: (scenario) => {
        set({ challengeScenario: scenario });
      },

      setEducationalModule: (module) => {
        set({ educationalModule: module });
      },

      setModeObjectives: (objectives) => {
        set({ modeObjectives: objectives });
      },

      updateModeProgress: (progress) => {
        set(state => ({
          modeProgress: {
            ...state.modeProgress,
            ...progress,
          },
        }));
      },

      addNotification: (notification) => {
        const newNotification = {
          ...notification,
          id: `notification-${Date.now()}-${Math.random()}`,
          timestamp: Date.now(),
        };
        
        set(state => ({
          ...state,
          notifications: [...state.notifications, newNotification]
        }));
      },

      markNotificationRead: (notificationId) => {
        set(state => ({
          notifications: state.notifications.map(n => 
            n.id === notificationId ? { ...n, read: true } : n
          )
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      processEvent: (event) => {
        const state = get();
        
        // Apply event effects
        const newStats = { ...state.stats };
        if (event.effects) {
          Object.entries(event.effects).forEach(([key, value]) => {
            if (value !== undefined) {
              if (key === 'debt') {
                // Debt can be negative (reducing debt)
                newStats[key as keyof PlayerStats] = Math.max(0, 
                  newStats[key as keyof PlayerStats] + value
                );
              } else {
                // Other stats are capped between 0-100
                newStats[key as keyof PlayerStats] = Math.max(0, Math.min(100, 
                  newStats[key as keyof PlayerStats] + value
                ));
              }
            }
          });
        }

        // Add timeline event
        const timelineEvent = {
          id: `event-${Date.now()}`,
          age: state.currentAge,
          type: 'milestone' as const,
          title: event.title,
          description: event.description,
          impact: event.effects || {},
          color: getEventColor(event.type),
        };

        // Add notification
        const notification = {
          type: 'event' as const,
          title: event.title,
          message: event.description,
          read: false,
        };

        set({
          stats: newStats,
          timeline: [...state.timeline, timelineEvent],
        });

        get().addNotification(notification);
      },

      updateStatus: (statusUpdate) => {
        set(state => ({
          status: { ...state.status, ...statusUpdate }
        }));
      },

      addMoney: (amount) => {
        set(state => ({
          money: state.money + amount
        }));
      },

      spendMoney: (amount) => {
        const state = get();
        // Allow spending even if it results in negative balance (debt)
        set({ money: state.money - amount });
        return true;
      },

      processEventChoice: (eventId, choiceId) => {
        const state = get();
        const event = gameEvents.find(e => e.id === eventId);
        const choice = event?.choices?.find(c => c.id === choiceId);
        
        if (!event || !choice) return;

        // Apply choice effects
        if (choice.effects) {
          const newStats = { ...state.stats };
          Object.entries(choice.effects).forEach(([stat, change]) => {
            if (stat in newStats) {
              if (stat === 'debt') {
                // Debt can be negative (reducing debt)
                newStats[stat as keyof PlayerStats] = Math.max(0, 
                  (newStats[stat as keyof PlayerStats] as number) + (change as number)
                );
              } else {
                // Other stats are capped between 0-100
                newStats[stat as keyof PlayerStats] = Math.max(0, Math.min(100, 
                  (newStats[stat as keyof PlayerStats] as number) + (change as number)
                ));
              }
            }
          });
          
          // Handle special effects
          if (choice.title.toLowerCase().includes('adopt') && choice.title.toLowerCase().includes('cat')) {
            set(state => ({
              stats: newStats,
              status: {
                ...state.status,
                hasPet: true,
                petType: 'Cat',
                petHappiness: 80,
                monthlyExpenses: state.status.monthlyExpenses + 50
              }
            }));
          } else if (choice.title.toLowerCase().includes('decline') && choice.title.toLowerCase().includes('cat')) {
            set({ stats: newStats });
          } else {
            set({ stats: newStats });
          }
        }

        // Handle costs
        if (choice.cost && choice.cost > 0) {
          get().spendMoney(choice.cost);
        }

        // Add notification
        get().addNotification({
          type: 'event',
          title: 'Event Decision Made',
          message: `You chose: ${choice.title}`,
          read: false,
        });

        // Recalculate net income
        get().calculateNetIncome();
      },

      calculateNetIncome: () => {
        const state = get();
        const netIncome = state.status.monthlyIncome - state.status.monthlyExpenses;
        set(state => ({
          status: { ...state.status, netIncome }
        }));
      },
    }),
    {
      name: 'sppefrun-life-game',
      partialize: (state) => ({
        currentStage: state.currentStage,
        currentAge: state.currentAge,
        stats: state.stats,
        completedDecisions: state.completedDecisions,
        availableDecisions: state.availableDecisions,
        gamePhase: state.gamePhase,
        score: state.score,
        unlockedAchievements: state.unlockedAchievements,
        timeline: state.timeline,
        settings: state.settings,
      }),
    }
  )
);

// Helper functions
function getCategoryColor(category: string): string {
  const colors = {
    health: '#ef4444',
    career: '#3b82f6',
    relationships: '#ec4899',
    education: '#8b5cf6',
    finance: '#10b981',
    lifestyle: '#f59e0b',
  };
  return colors[category as keyof typeof colors] || '#6b7280';
}

function getLifeStageForAge(age: number): string {
  if (age < 18) return 'childhood';
  if (age < 25) return 'early-adulthood';
  if (age < 35) return 'young-adult';
  if (age < 50) return 'middle-age';
  if (age < 65) return 'mature-adult';
  return 'senior';
}

function calculateScore(statChanges: Partial<PlayerStats>): number {
  return Object.values(statChanges).reduce((sum, change) => sum + (change || 0), 0);
}
